import { Injectable, signal, computed, effect } from '@angular/core';
import { EMPTY, Observable, expand, of, reduce } from 'rxjs';
import { map, tap, catchError, switchMap } from 'rxjs/operators';
import { Instance } from '@models/instance.model';
import { HttpClient } from '@angular/common/http';
import { environment } from '@env/environment';
import { Endpoint } from '@enums/endpoint.enum';
import { UserService } from '@services/user.service';
import { GridDataInterface, PaginationModel } from '@models/pagination.model';

const INSTANCE_APPENDS = 'logoMarkup,isCrifCheckAvailable,isUnitSyncEnabled';

@Injectable({
  providedIn: 'root'
})
export class InstanceService {

  private readonly _instances = signal<Instance[]>([]);
  readonly instances = this._instances.asReadonly();

  readonly currentInstance = computed(() => {
    const user = this.userService.me();
    const instances = this._instances();

    return instances.find(i => i.id === user?.instanceId)
      ?? user?.instance
      ?? null;
  });

  constructor(private http: HttpClient, private userService: UserService) {}

  getInstances(): Observable<Instance[]> {
    const perPage = 100;

    return this.getInstancesPage({ per_page: perPage, page: 1 }).pipe(
      expand(({ pagination }) =>
        pagination.currentPage < pagination.pageCount
          ? this.getInstancesPage({ per_page: perPage, page: pagination.currentPage + 1 })
          : EMPTY
      ),
      reduce((all, { data }) => all.concat(data), [] as Instance[]),
      tap(instances => this._instances.set(instances))
    );
  }

  getInstancesPage(queryParams: Record<string, any> = {}): Observable<GridDataInterface<Instance>> {
    const url = `${environment.apiBaseUrl}${Endpoint.INSTANCES}`;

    queryParams = {
      ...queryParams,
      append: INSTANCE_APPENDS,
    };

    return this.http.get(url, { params: queryParams }).pipe(
      map((res: any) => ({
        data: res?.data?.map((i: any) => new Instance(i)) ?? [],
        pagination: PaginationModel.fromApi(res?.meta?.pagination),
      })),
      catchError(() => of({ data: [], pagination: new PaginationModel() }))
    );
  }

  getPublicInstance(uuid: string, params: Record<string, string> = {}): Observable<Instance | null> {
    const url = `${environment.apiBaseUrl}/${uuid}/instance`;
    return this.http.get(url, { params }).pipe(
      map((res: any) => res?.data ? new Instance(res.data) : null),
      catchError(() => of(null))
    );
  }

  getInstance(id: number): Observable<Instance | null> {
    const url = `${environment.apiBaseUrl}${Endpoint.INSTANCES}/${id}`;

    const queryParams = {
      append: 'config',
    }

    return this.http.get(url, { params: queryParams }).pipe(
      map((res: any) => res?.data ? new Instance(res.data) : null)
    );
  }

  getFeatures(): Observable<{ key: string; label: string; description: string }[]> {
    return this.http.get<any>(`${environment.apiBaseUrl}${Endpoint.FEATURES}`).pipe(
      map(res => res ?? [])
    );
  }

  save(data: Partial<Instance>, coverImage?: File, logoImage?: File, faviconImage?: File): Observable<Instance | null> {
    const hasFiles = !!coverImage || !!logoImage || !!faviconImage;

    return hasFiles
      ? this.saveMultipart(data, coverImage, logoImage, faviconImage)
      : this.saveJson(data);
  }

  private saveJson(data: Partial<Instance>): Observable<Instance | null> {
    const isEdit = !!data.id;

    const url = isEdit
      ? `${environment.apiBaseUrl}${Endpoint.INSTANCES}/${data.id}`
      : `${environment.apiBaseUrl}${Endpoint.INSTANCES}`;

    const params = { append: INSTANCE_APPENDS };
    const request$ = isEdit
      ? this.http.patch(url, data, { params })
      : this.http.post(url, data, { params });

    return request$.pipe(
      map((res: any) => res?.data ? new Instance(res.data) : null),
      tap(saved => {
        if (saved) {
          if (isEdit) {
            this._instances.update(list =>
              list.map(i => i.id === saved.id ? saved : i)
            );
          } else {
            this._instances.update(list => [...list, saved]);
          }
        }
      })
    );
  }

  private saveMultipart(data: Partial<Instance>, coverImage?: File, logoImage?: File, faviconImage?: File): Observable<Instance | null> {
    const isEdit = !!data.id;

    const url = isEdit
      ? `${environment.apiBaseUrl}${Endpoint.INSTANCES}/${data.id}`
      : `${environment.apiBaseUrl}${Endpoint.INSTANCES}`;

    const formData = this.toFormData(data, coverImage, logoImage, faviconImage);

    if (isEdit) {
      formData.append('_method', 'PATCH');
    }

    return this.http.post(url, formData, { params: { append: INSTANCE_APPENDS } }).pipe(
      map((res: any) => res?.data ? new Instance(res.data) : null),
      tap(saved => {
        if (saved) {
          if (isEdit) {
            this._instances.update(list =>
              list.map(i => i.id === saved.id ? saved : i)
            );
          } else {
            this._instances.update(list => [...list, saved]);
          }
        }
      })
    );
  }

  deleteInstance(id: number): Observable<void> {
    const url = `${environment.apiBaseUrl}${Endpoint.INSTANCES}/${id}`;

    return this.http.delete<void>(url).pipe(
      tap(() => {
        this._instances.update(list => list.filter(i => i.id !== id));
      })
    );
  }

  selectInstance(instanceId: number): Observable<boolean> {
    const url = `${environment.apiBaseUrl}${Endpoint.PROFILE}`;

    return this.http.patch<{ data?: any }>(
      url,
      { instance_id: instanceId }
    ).pipe(
      switchMap(res => res?.data?.instanceId === instanceId
        ? this.userService.getMe().pipe(map(() => true))
        : of(false)
      )
    );
  }

  onInstanceChange(callback: () => void): void {
    let lastInstanceId: number | undefined;

    effect(() => {
      const instanceId = this.currentInstance()?.id;

      if (!instanceId) {
        lastInstanceId = undefined;
        return;
      }

      if (lastInstanceId !== instanceId) {
        lastInstanceId = instanceId;
        callback();
      }
    });
  }

  clear(): void {
    this._instances.set([]);
  }

  private toFormData(data: Record<string, any>, coverImage?: File, logoImage?: File, faviconImage?: File): FormData {
    const formData = new FormData();

    Object.entries(data).forEach(([key, value]) => {
      if (value !== null && value !== undefined) {
        if (typeof value === 'object' && !(value instanceof File)) {
          formData.append(key, JSON.stringify(value));
        } else {
          formData.append(key, String(value));
        }
      }
    });

    if (coverImage) {
      formData.append('cover_image', coverImage);
    }

    if (logoImage) {
      formData.append('logo_image', logoImage);
    }

    if (faviconImage) {
      formData.append('favicon_image', faviconImage);
    }

    return formData;
  }
}
